import DiscoveredCreatorCard from 'Components/creator-discovery/DiscoveredCreatorCard';
import AiPoweredBadge from 'Components/creator-discovery/AiPoweredBadge';
import DiscoveryResultsSummary from 'Components/creator-discovery/DiscoveryResultsSummary';
import DiscoveryStatusBanner from 'Components/creator-discovery/DiscoveryStatusBanner';
import RecentDiscoveryItem from 'Components/creator-discovery/RecentDiscoveryItem';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, UncontrolledTooltip } from 'reactstrap';
import { toast } from 'react-toastify';
import {
    discoverCreators,
    fetchCreatorDiscoveryDetails,
    fetchDiscoveredCreators,
    fetchRecentDiscoveries,
} from 'services/api/creators';
import {
    DiscoveredCreator,
    DiscoveryCreatorSort,
    DiscoveryDetails,
    DiscoveryView,
    getNextDiscoveryCreatorSort,
    getStoredDiscoveryView,
    isDiscoverySearched,
    RecentDiscovery,
    setStoredDiscoveryView,
} from 'types/creator-discovery';
import { getErrorMessage } from 'utils/helpers';
import './creator-discovery.scss';
import withAccessControl from 'hoc/withAccessControl';
import { PRIVILEGE } from 'constants/privilege';
import ApplicationView from 'Components/campaigns/applications/ApplicationView';
import { AppDispatch, RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { showCampaignInviteModal, toggleApplicationView } from 'slices/ui/reducer';
import { buildDiscoveredCreatorsCsv, downloadCsvFile } from 'utils/creatorDiscoveryCsvExport';
import ifConfigured from 'hoc/ifConfigured';
import eventEmitter from 'utils/eventEmitter';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 20 * 60 * 1000;
const CREATORS_SKELETON_COUNT = 8;

const CreatorsSkeleton = ({ view }: { view: DiscoveryView }) => {
    const isCardView = view === 'card';
    return (
        <div className={isCardView ? 'discovered-creators-grid' : 'discovered-creators-list'}>
            {Array.from({ length: CREATORS_SKELETON_COUNT }).map((_, index) => (
                <Skeleton
                    key={index}
                    height={isCardView ? 248 : 88}
                    className="rounded-3"
                />
            ))}
        </div>
    );
};

const CreatorDiscovery = () => {
    useDocumentTitle('Creator Discovery');

    const navigate = useNavigate();
    const { discoveryId } = useParams<{ discoveryId?: string }>();

    const [searchQuery, setSearchQuery] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [recentDiscoveries, setRecentDiscoveries] = useState<RecentDiscovery[]>([]);
    const [recentPage, setRecentPage] = useState(1);
    const [recentCount, setRecentCount] = useState(0);
    const [recentLoading, setRecentLoading] = useState(true);
    const [recentLoadingMore, setRecentLoadingMore] = useState(false);

    const [discoveryDetails, setDiscoveryDetails] = useState<DiscoveryDetails | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const [creators, setCreators] = useState<DiscoveredCreator[]>([]);
    const [creatorsPage, setCreatorsPage] = useState(1);
    const [creatorsCount, setCreatorsCount] = useState(0);
    const [creatorsLoading, setCreatorsLoading] = useState(false);
    const [creatorsLoadingMore, setCreatorsLoadingMore] = useState(false);
    const [creatorSort, setCreatorSort] = useState<DiscoveryCreatorSort>('rank');
    const [downloadingCreators, setDownloadingCreators] = useState(false);
    const [selectedCreatorIds, setSelectedCreatorIds] = useState<string[]>([]);
    const [resultsView, setResultsView] = useState<DiscoveryView>(getStoredDiscoveryView);

    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollStartedAtRef = useRef<number | null>(null);
    const pendingNewDiscoveryRef = useRef(false);
    const layoutSectionRef = useRef<HTMLDivElement>(null);
    const recentSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const debouncedRecentSearchRef = useRef('');
    const [scrollToResults, setScrollToResults] = useState(false);
    const [recentSearchInput, setRecentSearchInput] = useState('');
    const [debouncedRecentSearch, setDebouncedRecentSearch] = useState('');
    const applicationView = useSelector((state: RootState) => state.ui.applicationView);
    const agencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const dispatch: AppDispatch = useDispatch();

    const loadRecentDiscoveries = useCallback(async (page: number, q: string, append = false) => {
        if (!agencyId) return;
        if (append) {
            setRecentLoadingMore(true);
        } else {
            setRecentLoading(true);
        }

        try {
            const data = await fetchRecentDiscoveries(agencyId, page, q || undefined);
            setRecentCount(data.count);
            setRecentPage(page);
            setRecentDiscoveries((prev) => (append ? [...prev, ...data.items] : data.items));
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to load recent discoveries'));
        } finally {
            setRecentLoading(false);
            setRecentLoadingMore(false);
        }
    }, []);

    const loadDiscoveryDetails = useCallback(async (id: string, silent = false) => {
        if (!agencyId) return;
        if (!silent) {
            setDetailsLoading(true);
        }
        try {
            const details = await fetchCreatorDiscoveryDetails(agencyId, id);
            setDiscoveryDetails(details);
            return details;
        } catch (error) {
            if (!silent) {
                toast.error(getErrorMessage(error, 'Failed to load discovery details'));
                setDiscoveryDetails(null);
            }
            return null;
        } finally {
            if (!silent) {
                setDetailsLoading(false);
            }
        }
    }, []);

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        pollStartedAtRef.current = null;
    }, []);

    const loadCreators = useCallback(async (
        id: string,
        page: number,
        sort: DiscoveryCreatorSort,
        append = false
    ) => {
        if (!agencyId) return;
        if (append) {
            setCreatorsLoadingMore(true);
        } else {
            setCreatorsLoading(true);
        }

        try {
            const data = await fetchDiscoveredCreators(agencyId, id, page, sort);
            setCreatorsCount(data.count);
            setCreatorsPage(page);
            setCreators((prev) => (append ? [...prev, ...data.items] : data.items));
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to load creators'));
        } finally {
            setCreatorsLoading(false);
            setCreatorsLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        if (!discoveryId || !scrollToResults) return;

        const timer = window.setTimeout(() => {
            layoutSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setScrollToResults(false);
        }, 100);

        return () => window.clearTimeout(timer);
    }, [discoveryId, scrollToResults]);

    useEffect(() => {
        setCreatorSort('rank');
        setSelectedCreatorIds([]);
    }, [discoveryId]);

    useEffect(() => {
        const clearSelection = () => setSelectedCreatorIds([]);
        eventEmitter.on('campaign-invite-success', clearSelection);
        return () => {
            eventEmitter.off('campaign-invite-success', clearSelection);
        };
    }, []);

    useEffect(() => {
        if (recentSearchDebounceRef.current) {
            clearTimeout(recentSearchDebounceRef.current);
        }

        recentSearchDebounceRef.current = setTimeout(() => {
            const trimmed = recentSearchInput.trim();
            setDebouncedRecentSearch(trimmed.length >= 3 ? trimmed : '');
        }, 1000);

        return () => {
            if (recentSearchDebounceRef.current) {
                clearTimeout(recentSearchDebounceRef.current);
            }
        };
    }, [recentSearchInput]);

    useEffect(() => {
        debouncedRecentSearchRef.current = debouncedRecentSearch;
    }, [debouncedRecentSearch]);

    useEffect(() => {
        loadRecentDiscoveries(1, debouncedRecentSearch);
    }, [debouncedRecentSearch, loadRecentDiscoveries]);

    useEffect(() => {
        if (!discoveryId) {
            setDiscoveryDetails(null);
            setCreators([]);
            setCreatorsCount(0);
            setCreatorsPage(1);
            return;
        }

        let isCancelled = false;

        const initDiscovery = async () => {
            setDiscoveryDetails(null);
            setCreators([]);
            setCreatorsCount(0);
            setCreatorsPage(1);
            setCreatorSort('rank');

            const details = await loadDiscoveryDetails(discoveryId);
            if (isCancelled || !details) return;

            if (isDiscoverySearched(details.state)) {
                await loadCreators(discoveryId, 1, 'rank');
                if (pendingNewDiscoveryRef.current) {
                    pendingNewDiscoveryRef.current = false;
                    await loadRecentDiscoveries(1, debouncedRecentSearchRef.current);
                }
            }
        };

        initDiscovery();

        return () => {
            isCancelled = true;
        };
    }, [discoveryId, loadDiscoveryDetails, loadCreators, loadRecentDiscoveries]);

    useEffect(() => {
        stopPolling();

        if (!discoveryId || isDiscoverySearched(discoveryDetails?.state ?? '')) {
            return;
        }

        pollStartedAtRef.current = Date.now();

        pollIntervalRef.current = setInterval(async () => {
            if (
                pollStartedAtRef.current &&
                Date.now() - pollStartedAtRef.current >= MAX_POLL_DURATION_MS
            ) {
                stopPolling();
                toast.info('Discovery is taking longer than expected. Please check back later.');
                return;
            }

            const details = await loadDiscoveryDetails(discoveryId, true);
            if (details && isDiscoverySearched(details.state)) {
                stopPolling();
                await loadCreators(discoveryId, 1, creatorSort);
                if (pendingNewDiscoveryRef.current) {
                    pendingNewDiscoveryRef.current = false;
                    await loadRecentDiscoveries(1, debouncedRecentSearchRef.current);
                }
            }
        }, POLL_INTERVAL_MS);

        return stopPolling;
    }, [discoveryId, isDiscoverySearched(discoveryDetails?.state ?? ''), creatorSort, loadDiscoveryDetails, loadCreators, loadRecentDiscoveries, stopPolling]);

    const handleSortToggle = () => {
        const nextSort = getNextDiscoveryCreatorSort(creatorSort);
        setCreatorSort(nextSort);
        setSelectedCreatorIds([]);
        if (discoveryId && isDiscoverySearched(discoveryDetails?.state ?? '')) {
            setCreators([]);
            setCreatorsPage(1);
            loadCreators(discoveryId, 1, nextSort);
        }
    };

    const handleSearchSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!agencyId) return;
        const trimmedQuery = searchQuery.trim();
        if (!trimmedQuery) {
            toast.info('Please enter a search query');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await discoverCreators(agencyId, trimmedQuery);
            if (response.status && response.uuid) {
                setSearchQuery('');
                pendingNewDiscoveryRef.current = true;
                setScrollToResults(true);
                navigate(`/creator-discovery/${response.uuid}`);
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to start creator discovery'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSelectDiscovery = (id: string) => {
        if (id !== discoveryId) {
            navigate(`/creator-discovery/${id}`);
        }
    };

    const loadMoreRecent = () => {
        if (recentDiscoveries.length < recentCount && !recentLoadingMore) {
            loadRecentDiscoveries(recentPage + 1, debouncedRecentSearch, true);
        }
    };

    const loadMoreCreators = () => {
        if (discoveryId && creators.length < creatorsCount && !creatorsLoadingMore) {
            loadCreators(discoveryId, creatorsPage + 1, creatorSort, true);
        }
    };

    const downloadCreators = async () => {
        if (!discoveryId || !agencyId || downloadingCreators || creatorsCount === 0) return;

        setDownloadingCreators(true);
        try {
            const { items } = await fetchDiscoveredCreators(agencyId, discoveryId, 1, creatorSort, creatorsCount);
            const csv = buildDiscoveredCreatorsCsv(items);
            const date = new Date().toISOString().split('T')[0];
            downloadCsvFile(csv, `creators_${discoveryId}_${date}.csv`);
            toast.success('Creators list downloaded successfully.');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to download creators'));
        } finally {
            setDownloadingCreators(false);
        }
    };

    const handleToggleCreatorSelect = (creatorId: string) => {
        setSelectedCreatorIds((prev) =>
            prev.includes(creatorId)
                ? prev.filter((id) => id !== creatorId)
                : [...prev, creatorId]
        );
    };

    const handleClearSelection = () => {
        setSelectedCreatorIds([]);
    };

    const handleCreatorBookmarkChange = useCallback((creatorId: string, isBookmarked: boolean) => {
        setCreators((prev) =>
            prev.map((creator) =>
                creator.uuid === creatorId ? { ...creator, is_bookmarked: isBookmarked } : creator
            )
        );
    }, []);

    useEffect(() => {
        const clearBookmark = ({ creatorId }: { creatorId: string }) => {
            handleCreatorBookmarkChange(creatorId, false);
        };
        eventEmitter.on('creator-removed-from-collection', clearBookmark);
        return () => {
            eventEmitter.off('creator-removed-from-collection', clearBookmark);
        };
    }, [handleCreatorBookmarkChange]);

    const handleResultsViewChange = (view: DiscoveryView) => {
        setResultsView(view);
        setStoredDiscoveryView(view);
    };

    const handleInviteToCampaign = () => {
        if (selectedCreatorIds.length === 0) return;
        const selectedCreators = creators
            .filter((creator) => selectedCreatorIds.includes(creator.uuid))
            .map((creator) => ({
                uuid: creator.uuid,
                name: creator.name,
                handle: creator.handle,
                profile_picture_url: creator.profile_picture_url,
                platform: creator.platform,
                followers_count: creator.followers_count,
            }));
        dispatch(
            showCampaignInviteModal({
                socialAccountIds: selectedCreatorIds,
                creators: selectedCreators,
            })
        );
    };

    const isSearched = isDiscoverySearched(discoveryDetails?.state ?? '');
    const showCreatorsGrid = isSearched && creators.length > 0;
    const showCreatorsEmpty = isSearched && !creatorsLoading && creators.length === 0;
    const hasSelection = selectedCreatorIds.length > 0;
    const creatorsLayoutClass = resultsView === 'card' ? 'discovered-creators-grid' : 'discovered-creators-list';

    return (
        <>
            <div className="page-content creator-discovery-page">
                <Container fluid>
                    <section className="creator-discovery-hero">
                        <div className="creator-discovery-hero__title-row">
                            <h1 className="creator-discovery-hero__title">Creator Discovery</h1>
                            <AiPoweredBadge />
                        </div>
                        <p className="creator-discovery-hero__subtitle">
                            AI-powered search - describe the creators you need in natural language: location, niche,
                            audience, follower range, and more.
                        </p>
                        <form className="creator-discovery-search" onSubmit={handleSearchSubmit}>
                            <textarea
                                className="creator-discovery-search__textarea"
                                placeholder="e.g. Noida based influencers, well English spoken, good personality, women only..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                disabled={isSubmitting}
                            />
                            <div className="creator-discovery-search__actions">
                                <button
                                    type="submit"
                                    className="btn btn-primary creator-discovery-search__btn"
                                    disabled={isSubmitting || !searchQuery.trim()}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" />
                                            Discovering...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-search-2-line" />
                                            Discover Creators
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </section>

                    <div ref={layoutSectionRef} className="creator-discovery-layout">
                        <aside className="creator-discovery-sidebar">
                            <div className="creator-discovery-sidebar__header">
                                <h2 className="creator-discovery-sidebar__title">Recent Searches</h2>
                                {recentCount > 0 && (
                                    <span className="creator-discovery-sidebar__count">{recentCount}</span>
                                )}
                            </div>
                            <div className="creator-discovery-sidebar__search">
                                <div className="creator-discovery-sidebar__search-field">
                                    <input
                                        type="text"
                                        className="creator-discovery-sidebar__search-input"
                                        placeholder="Search recent..."
                                        value={recentSearchInput}
                                        onChange={(e) => setRecentSearchInput(e.target.value)}
                                    />
                                    <i className="ri-search-2-line creator-discovery-sidebar__search-icon" />
                                </div>
                            </div>
                            <div id="recent-discoveries-scroll" className="creator-discovery-sidebar__list">
                                {recentLoading ? (
                                    Array.from({ length: 6 }).map((_, index) => (
                                        <div key={index} className="p-2">
                                            <Skeleton height={56} className="rounded-3" />
                                        </div>
                                    ))
                                ) : recentDiscoveries.length > 0 ? (
                                    <InfiniteScroll
                                        dataLength={recentDiscoveries.length}
                                        next={loadMoreRecent}
                                        hasMore={recentDiscoveries.length < recentCount}
                                        loader={
                                            <div className="p-2">
                                                <Skeleton height={56} className="rounded-3" />
                                            </div>
                                        }
                                        scrollableTarget="recent-discoveries-scroll"
                                    >
                                        {recentDiscoveries.map((discovery) => (
                                            <RecentDiscoveryItem
                                                key={discovery.uuid}
                                                discovery={discovery}
                                                isActive={discovery.uuid === discoveryId}
                                                onClick={() => handleSelectDiscovery(discovery.uuid)}
                                            />
                                        ))}
                                    </InfiniteScroll>
                                ) : (
                                    <div className="creator-discovery-empty py-4">
                                        <p className="creator-discovery-empty__text mb-0">
                                            {debouncedRecentSearch ? 'No matching searches found' : 'No searches yet'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </aside>

                        <main className="creator-discovery-results">
                            <div className="creator-discovery-results__header">
                                <div className="creator-discovery-results__header-left">
                                    <h2 className="creator-discovery-results__title">Results</h2>
                                    {isSearched && creatorsCount > 0 && (
                                        <span className="creator-discovery-results__count">{creatorsCount} creators</span>
                                    )}
                                    {hasSelection && (
                                        <>
                                            <span className="creator-discovery-results__selected-count">
                                                {selectedCreatorIds.length} selected
                                            </span>
                                            <button
                                                type="button"
                                                className="creator-discovery-results__clear"
                                                onClick={handleClearSelection}
                                            >
                                                Clear
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div className="creator-discovery-results__header-actions">
                                    <div
                                        className="creator-discovery-view-toggle"
                                        role="group"
                                        aria-label="Results layout"
                                    >
                                        <button
                                            type="button"
                                            className={`creator-discovery-view-toggle__btn${resultsView === 'list' ? ' creator-discovery-view-toggle__btn--active' : ''}`}
                                            aria-label="List view"
                                            aria-pressed={resultsView === 'list'}
                                            onClick={() => handleResultsViewChange('list')}
                                        >
                                            <i className="ri-list-unordered" />
                                        </button>
                                        <button
                                            type="button"
                                            className={`creator-discovery-view-toggle__btn${resultsView === 'card' ? ' creator-discovery-view-toggle__btn--active' : ''}`}
                                            aria-label="Card view"
                                            aria-pressed={resultsView === 'card'}
                                            onClick={() => handleResultsViewChange('card')}
                                        >
                                            <i className="ri-grid-line" />
                                        </button>
                                    </div>
                                    <span
                                        id="invite-to-campaign-btn"
                                        className="d-inline-block"
                                    >
                                        <button
                                            type="button"
                                            disabled={!hasSelection}
                                            className="btn btn-primary creator-discovery-results__action-btn"
                                            onClick={handleInviteToCampaign}
                                            style={{ pointerEvents: hasSelection ? 'auto' : 'none' }}
                                        >
                                            <i className="ri-mail-send-line" />
                                            Invite to Campaign
                                        </button>
                                    </span>
                                    {!hasSelection && (
                                        <UncontrolledTooltip
                                            placement="top"
                                            target="invite-to-campaign-btn"
                                            fade={false}
                                            autohide={false}
                                        >
                                            {creatorsCount === 0
                                                ? "No discovered creators found. Run a search to discover creators first."
                                                : "Select at least one creator to invite them to a campaign."}
                                        </UncontrolledTooltip>
                                    )}

                                    {/* <button
                                        type="button"
                                        className="creator-discovery-results__download-btn"
                                        onClick={downloadCreators}
                                        disabled={downloadingCreators || creatorsCount === 0}
                                    >
                                        <i className="ri-download-2-line" />
                                        <span>{downloadingCreators ? 'Downloading...' : 'Download'}</span>
                                    </button> */}
                                </div>
                            </div>

                            <div id="discovered-creators-scroll" className="creator-discovery-results__body">
                                {!discoveryId ? (
                                    <div className="creator-discovery-empty">
                                        <div className="creator-discovery-empty__icon">
                                            <i className="ri-user-search-line" />
                                        </div>
                                        <h3 className="creator-discovery-empty__title">Start a new discovery</h3>
                                        <p className="creator-discovery-empty__text">
                                            Enter your creator requirements above or pick a recent search from the sidebar to
                                            view matched creators.
                                        </p>
                                    </div>
                                ) : detailsLoading && !discoveryDetails ? (
                                    <>
                                        <Skeleton height={100} className="rounded-3 mb-3" />
                                        <CreatorsSkeleton view={resultsView} />
                                    </>
                                ) : discoveryDetails ? (
                                    <>
                                        {isSearched && (
                                            <DiscoveryResultsSummary
                                                query={discoveryDetails.query}
                                                sort={creatorSort}
                                                onSortToggle={handleSortToggle}
                                            />
                                        )}

                                        {!isSearched && (
                                            <DiscoveryStatusBanner
                                                state={discoveryDetails.state}
                                            />
                                        )}

                                        {!isSearched && (
                                            <CreatorsSkeleton view={resultsView} />
                                        )}

                                        {showCreatorsGrid && (
                                            <InfiniteScroll
                                                dataLength={creators.length}
                                                next={loadMoreCreators}
                                                hasMore={creators.length < creatorsCount}
                                                scrollableTarget="discovered-creators-scroll"
                                                loader={
                                                    <div className="creator-discovery-load-more">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                                    </div>
                                                }
                                            >
                                                <div className={creatorsLayoutClass}>
                                                    {creators.map((creator) => (
                                                        <DiscoveredCreatorCard
                                                            key={creator.uuid}
                                                            creator={creator}
                                                            view={resultsView}
                                                            isSelected={selectedCreatorIds.includes(creator.uuid)}
                                                            onToggleSelect={handleToggleCreatorSelect}
                                                            onBookmarkChange={handleCreatorBookmarkChange}
                                                        />
                                                    ))}
                                                </div>
                                            </InfiniteScroll>
                                        )}

                                        {creatorsLoading && isSearched && creators.length === 0 && (
                                            <CreatorsSkeleton view={resultsView} />
                                        )}

                                        {showCreatorsEmpty && (
                                            <div className="creator-discovery-empty">
                                                <div className="creator-discovery-empty__icon">
                                                    <i className="ri-user-unfollow-line" />
                                                </div>
                                                <h3 className="creator-discovery-empty__title">No creators found</h3>
                                                <p className="creator-discovery-empty__text">
                                                    We couldn&apos;t find creators matching this query. Try refining your
                                                    search with more specific criteria.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                ) : null}
                            </div>
                        </main>
                    </div>
                </Container>
            </div>
            <ApplicationView
                isOpen={!!applicationView}
                socialAccountId={applicationView?.socialAccountId!}
                toggleOffcanvas={() => dispatch(toggleApplicationView(null))}
                showPermalink={false}
            />
        </>
    );
};

export default ifConfigured(CreatorDiscovery, 'agency_creator_discovery');